import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { formatBRL } from '../consultation/plans'
import { fetchPricing, type ConsultationPricing } from './pricingApi'
import { PricingContext, type PricingContextValue } from './PricingContext'

/** Exibido enquanto o preço não chega do backend: nunca mostramos um valor inventado. */
const PRICE_PLACEHOLDER = 'R$ --,--'

type PricingProviderProps = {
  children: ReactNode
  /**
   * Preço inicial: o gravado no HTML pelo build ou, na pré-renderização, o buscado no backend.
   * O preço atualizado é sempre buscado de novo no navegador.
   */
  initialPricing?: ConsultationPricing | null
}

/**
 * Carrega o preço uma vez, ao abrir o site, e disponibiliza para todos os componentes.
 * O preço é definido exclusivamente no .env do backend.
 */
export function PricingProvider({ children, initialPricing = null }: PricingProviderProps) {
  const [pricing, setPricing] = useState<ConsultationPricing | null>(initialPricing)
  const [status, setStatus] = useState<PricingContextValue['status']>(initialPricing ? 'ready' : 'loading')

  useEffect(() => {
    const controller = new AbortController()

    fetchPricing(controller.signal)
      .then((data) => {
        setPricing(data)
        setStatus('ready')
      })
      .catch(() => {
        // Com o preço do build em mãos, seguimos com ele; sem ele, sinalizamos o erro.
        if (!controller.signal.aborted && !initialPricing) setStatus('error')
      })

    return () => controller.abort()
  }, [initialPricing])

  const value = useMemo<PricingContextValue>(() => {
    const consultation = pricing?.completeConsultation
    const price = consultation?.price ?? null
    const originalPrice = consultation?.originalPrice ?? null

    return {
      status,
      price,
      originalPrice,
      discountPercent: consultation?.discountPercent ?? null,
      priceLabel: price === null ? PRICE_PLACEHOLDER : formatBRL(price),
      originalPriceLabel: originalPrice === null ? null : formatBRL(originalPrice),
      hasDiscount: originalPrice !== null,
    }
  }, [pricing, status])

  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>
}
